/* eslint-disable no-return-assign */
import React, { useState, useCallback, lazy, Suspense, useRef } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// ReactCSV
import { CSVLink } from 'react-csv';

// Material
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

// Helpers
import { formatDateAndTime } from 'helpers/date';

// Actions
import * as ReportActions from 'store/ducks/reports/actions';
import { setOpen, setClose } from 'store/ducks/dialogs/actions';

// Types
import { ApplicationState } from 'store';

// Constants
import { REPORTS_TYPES } from 'constants/report';
import ITEMS from 'constants/itemsByFilter';
import TITLES_OPTIONS from 'constants/infoModulesTitle';
import TRIP_OR_TICKET from 'constants/tripOrTicket';

// Components
import { MainContent, SearchComponent, ReportTitle } from 'components';
import { TablePlaceholder } from 'components/TablePlaceholder';
import { TabPanel } from 'components/TabPanel';
import { Submissions, History } from '../../../../components';

// Styles
import useStyles from './SegmentDemandReportDatails.styles';

// Lazy Components
const TableSegmentDemandReport = lazy(() =>
  import('./components/TableSegmentDemandReport/TableSegmentDemandReport'),
);
const Filter = lazy(() => import('components/Filter'));

interface SegmentDemandReportDatailsProps {
  setSubmissionData: (param: any) => void;
  setIsAddSubmissiion: (param: any) => void;
  setIsEditSubmissiion: (param: any) => void;
}

const SegmentDemandReportDatails: React.FC<SegmentDemandReportDatailsProps> = ({
  setSubmissionData,
  setIsAddSubmissiion,
  setIsEditSubmissiion,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const csvRef = useRef(null);

  const [generalNavGuide, setGeneralNavGuide] = useState(0);
  const [tableReference, setTableReference] = useState<unknown>();
  const [reportIsVisible, setReportIsVisible] = useState(false);
  const [tableCSV, setTableCSV] = useState('');
  const [periodString, setPeriodString] = useState('');

  const dialog = useSelector((state: ApplicationState) => state.dialogs.dialog);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGeneralNavGuide(newValue);
  };

  const handleLoadData = useCallback(() => {
    setReportIsVisible(true);
    dispatch(
      ReportActions.lineDemandReportRequest({
        startDate: new Date().setHours(0, 0, 0),
        endDate: new Date().setHours(23, 59, 59),
        segment: true,
      }),
    );

    const tempPeriodString = `Dia: ${new Date().toLocaleDateString('pt-BR', {
      dateStyle: 'short',
    })}`;

    setPeriodString(tempPeriodString);
  }, [dispatch]);

  const handleApply = useCallback(
    (data: any) => {
      dispatch(setClose({}));
      setReportIsVisible(true);

      const {
        startedAt,
        startDate,
        endDate,
        selectedLines,
        selectedSeccionedLine,
        selectedTripOrTicket,
        selectedEmployees,
      } = data;
      let startDateValue;
      let endDateValue;
      const isPeriod = startDate?.getTime() < endDate?.getTime();
      if (isPeriod) {
        startDateValue = new Date(startDate).getTime();
        endDateValue = new Date(endDate).getTime();
      } else if (startedAt) {
        startDateValue = new Date(startedAt).setHours(0, 0, 0);
        endDateValue = new Date(startedAt).setHours(23, 59, 59);
      }

      dispatch(
        ReportActions.lineDemandReportRequest({
          startDate: startDateValue,
          endDate: endDateValue,
          lineId:
            selectedLines?.length > 0
              ? selectedLines.map((e: any) => e.id).join()
              : undefined,
          employeeId:
            selectedEmployees?.length > 0
              ? selectedEmployees.map((e: any) => e.id).join()
              : undefined,
          seccioned: Boolean(selectedSeccionedLine),
          timeBy: selectedTripOrTicket,
          segment: true,
        }),
      );

      const tempPeriodString = `De: ${
        isPeriod
          ? formatDateAndTime(new Date(startDate))
          : formatDateAndTime(new Date(startedAt))
      } até: ${
        isPeriod
          ? formatDateAndTime(new Date(endDate))
          : formatDateAndTime(new Date(startedAt))
      }`;

      let updatedPeriodString = tempPeriodString;

      if (selectedLines?.length > 0) {
        const tempLinesString = `Linhas: ${selectedLines
          .map((e: any) => e.name)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      if (selectedTripOrTicket !== '') {
        const tempLinesString = `Tipo de Filtro: ${
          Object.values(TRIP_OR_TICKET.TYPE).find(
            (s) => s.KEY === selectedTripOrTicket,
          )?.VALUE
        }`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      if (selectedEmployees?.length > 0) {
        console.log('selectedEmployees', selectedEmployees);
        const tempEmployeesString = `Motoristas: ${selectedEmployees
          .map((e: any) => e.name)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempEmployeesString;
      }

      setPeriodString(updatedPeriodString);
    },
    [dispatch],
  );

  return (
    <div className={classes.root}>
      <MainContent className={classes.paperRoot}>
        <Grid
          container
          justifyContent='space-between'
          className={classes.gridContainer}>
          <Grid container className={classes.paperTitle}>
            <ReportTitle
              titleVariant='h4'
              title='Relatórios de Operação • Demanda por Trecho'
              tooltipTitle={
                TITLES_OPTIONS.REPORTS.OPERATION.DEMAND_PER_SECTION.VALUE
              }
            />
          </Grid>
          <Grid item>
            <CSVLink
              filename='Relatório_demanda_por_trecho.csv'
              data={tableCSV}
              ref={csvRef}
            />
          </Grid>
        </Grid>

        <Grid container direction='column'>
          <Grid item className={classes.scopeFilter}>
            <Box
              display='flex'
              alignItems='center'
              width='100%'
              marginTop='8px'>
              <SearchComponent
                disabledInput={!reportIsVisible}
                modalFilter={() => {
                  dispatch(
                    setOpen({
                      open: true,
                      content: (
                        <Suspense fallback={<p>Carregando...</p>}>
                          <Filter
                            fields={ITEMS.segmentDemand}
                            handleApply={handleApply}
                          />
                        </Suspense>
                      ),
                      maxWidth: 'xl',
                    }),
                  );
                }}
              />
              {/* <Grid container justifyContent='flex-end'>
                <Button
                  className={classes.addButton}
                  onClick={() => setIsAddSubmissiion(true)}>
                  <Typography style={{ marginRight: 8, fontSize: 20 }}>
                    +
                  </Typography>{' '}
                  <Typography style={{ fontSize: 12 }}>
                    Programar Novo
                  </Typography>
                </Button>
              </Grid> */}
            </Box>
          </Grid>
        </Grid>
        <Tabs
          indicatorColor='primary'
          onChange={handleChange}
          style={{
            background: '#ffffff',
            margin: '10px 0px 32px 16px',
          }}
          textColor='primary'
          value={generalNavGuide}>
          <Tab label='Relatório' />
          <Tab label='Envios' />
          <Tab label='Histórico' />
        </Tabs>

        <TabPanel index={0} value={generalNavGuide}>
          {!reportIsVisible && <TablePlaceholder onClick={handleLoadData} />}
          {reportIsVisible && (
            <>
              <Suspense fallback={<p>Loading...</p>}>
                <Grid ref={(node: unknown) => setTableReference(node)}>
                  {dialog.open && dialog.isDialogPrint && (
                    <Typography color='inherit' variant='h4'>
                      Relatórios de Operação • Demanda por Trecho
                    </Typography>
                  )}
                  <TableSegmentDemandReport
                    csvRef={csvRef}
                    tableReference={tableReference}
                    setTableCSV={setTableCSV}
                    periodString={periodString}
                  />
                </Grid>
              </Suspense>
            </>
          )}
        </TabPanel>
        <TabPanel index={1} value={generalNavGuide}>
          <Submissions
            setSubmissionData={setSubmissionData}
            setIsEditSubmissiion={setIsEditSubmissiion}
            setIsAddSubmissiion={setIsAddSubmissiion}
            reportType={REPORTS_TYPES.ENTRY_SECCIONED}
          />
        </TabPanel>
        <TabPanel index={2} value={generalNavGuide}>
          <History reportType={REPORTS_TYPES.ENTRY_SECCIONED} />
        </TabPanel>
      </MainContent>
    </div>
  );
};

export { SegmentDemandReportDatails };
export default SegmentDemandReportDatails;
